import type { AgentOption, AgentOptionsResponse, BrainDecision, BrainPolicy, BrainTestResult, ProcessingStage, RankingWeights, RetrievalStage } from './types'

const defaultRankingWeights: RankingWeights = {
  exact: 0,
  attributes: 0,
  text: 0,
  semantic: 0,
  availability: 0,
  popularity: 0,
  conversion: 0,
  recency: 0,
  commercial: 0,
}

const defaultAudioMimeTypes = ['audio/ogg', 'audio/opus', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/webm']

export function normalizeArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : []
}

export function normalizeBrainPolicy(policy: BrainPolicy): BrainPolicy {
  return {
    ...policy,
    tryOnRoutingEnabled: policy.tryOnRoutingEnabled ?? true,
    eyewearRecognitionEnabled: policy.eyewearRecognitionEnabled ?? true,
    apparelRecognitionEnabled: policy.apparelRecognitionEnabled ?? true,
    tryOnAutoLinkEnabled: policy.tryOnAutoLinkEnabled ?? true,
    tryOnClarificationEnabled: policy.tryOnClarificationEnabled ?? true,
    tryOnConfidenceMin: typeof policy.tryOnConfidenceMin === 'number' ? policy.tryOnConfidenceMin : 0.8,
    rankingWeights: policy.rankingWeights && typeof policy.rankingWeights === 'object' ? { ...defaultRankingWeights, ...policy.rankingWeights } : { ...defaultRankingWeights },
    requiredFields: normalizeArray(policy.requiredFields),
    secondaryFields: normalizeArray(policy.secondaryFields),
    allowedImageMimeTypes: normalizeArray(policy.allowedImageMimeTypes),
    allowedAudioMimeTypes: normalizeArray(policy.allowedAudioMimeTypes).length > 0 ? normalizeArray(policy.allowedAudioMimeTypes) : [...defaultAudioMimeTypes],
  }
}

export function normalizeBrainDecision(decision: BrainDecision): BrainDecision {
  return {
    ...decision,
    inputType: normalizeText(decision.inputType, 'UNKNOWN').toUpperCase(),
    normalizedInput: normalizeText(decision.normalizedInput),
    intent: normalizeText(decision.intent, 'unknown'),
    intentConfidence: normalizeNumber(decision.intentConfidence),
    attributeConfidence: normalizeNumber(decision.attributeConfidence),
    retrievalConfidence: normalizeNumber(decision.retrievalConfidence),
    resultCode: normalizeText(decision.resultCode, 'UNKNOWN'),
    retrievalStages: normalizeArray(decision.retrievalStages).map(normalizeRetrievalStage),
    processingStages: normalizeArray(decision.processingStages).map(normalizeProcessingStage),
    inputMetadata: normalizeRecord(decision.inputMetadata),
    failureStage: normalizeOptionalText(decision.failureStage),
    failureDetail: normalizeOptionalText(decision.failureDetail),
    relaxedFields: normalizeArray(decision.relaxedFields),
    decisionStatus: normalizeText(decision.decisionStatus, 'UNKNOWN'),
    selectedProductIds: normalizeArray(decision.selectedProductIds),
    queryPlan: normalizeRecord(decision.queryPlan),
    latencyMs: normalizeNumber(decision.latencyMs),
    inputTokens: normalizeNumber(decision.inputTokens),
    outputTokens: normalizeNumber(decision.outputTokens),
    estimatedCost: normalizeNumber(decision.estimatedCost),
    failureCode: normalizeOptionalText(decision.failureCode),
    correlationId: normalizeText(decision.correlationId, 'indisponível'),
  }
}

export function normalizeBrainTestResult(result: BrainTestResult): BrainTestResult {
  const agent = result.agent ?? {
    inputTokens: 0,
    outputTokens: 0,
    latencyMs: 0,
    fallbackUsed: false,
    estimatedCost: 0,
  }
  const intent = result.intent ?? { intent: 'unknown', confidence: 0, entities: {} }
  return {
    ...result,
    intent: {
      ...intent,
      intent: normalizeText(intent.intent, 'unknown'),
      confidence: normalizeNumber(intent.confidence),
      entities: normalizeRecord(intent.entities),
    },
    products: normalizeArray(result.products),
    status: normalizeText(result.status, 'UNKNOWN'),
    resultCode: normalizeText(result.resultCode, 'UNKNOWN'),
    attributeConfidence: normalizeNumber(result.attributeConfidence),
    retrievalConfidence: normalizeNumber(result.retrievalConfidence),
    retrievalStages: normalizeArray(result.retrievalStages).map(normalizeRetrievalStage),
    processingStages: normalizeArray(result.processingStages).map(normalizeProcessingStage),
    inputMetadata: normalizeRecord(result.inputMetadata),
    failureStage: normalizeOptionalText(result.failureStage),
    failureDetail: normalizeOptionalText(result.failureDetail),
    relaxedFields: normalizeArray(result.relaxedFields),
    queryPlan: normalizeRecord(result.queryPlan),
    agent: {
      ...agent,
      inputTokens: normalizeNumber(agent.inputTokens),
      outputTokens: normalizeNumber(agent.outputTokens),
      latencyMs: normalizeNumber(agent.latencyMs),
      fallbackUsed: Boolean(agent.fallbackUsed),
      estimatedCost: normalizeNumber(agent.estimatedCost),
    },
    latencyMs: normalizeNumber(result.latencyMs),
    correlationId: normalizeText(result.correlationId, 'indisponível'),
  }
}

export function normalizeAgentOption(option: AgentOption): AgentOption {
  return {
    ...option,
    capabilities: normalizeArray(option.capabilities).map((capability) => normalizeText(capability).toUpperCase()).filter(Boolean),
    capabilityEvidence: normalizeArray(option.capabilityEvidence).map((evidence) => ({ capability: normalizeText(evidence.capability).toUpperCase(), source: normalizeText(evidence.source, 'UNKNOWN').toUpperCase() })),
    blockingReasons: normalizeArray(option.blockingReasons).map((reason) => normalizeText(reason).toUpperCase()).filter(Boolean),
    enabled: Boolean(option.enabled),
    providerReady: Boolean(option.providerReady),
    credentialReady: Boolean(option.credentialReady),
    healthReady: Boolean(option.healthReady),
    modelReady: Boolean(option.modelReady),
    capabilityEligible: Boolean(option.capabilityEligible),
    tenantEligible: Boolean(option.tenantEligible),
    environmentEligible: Boolean(option.environmentEligible),
    slotEligible: Boolean(option.slotEligible),
    runtimeReady: Boolean(option.runtimeReady),
    ready: Boolean(option.ready),
    selectable: Boolean(option.selectable),
    readinessCode: normalizeText(option.readinessCode, 'READINESS_UNKNOWN').toUpperCase(),
    eligibilityCode: normalizeText(option.eligibilityCode, 'ELIGIBILITY_UNKNOWN').toUpperCase(),
    recommendedAction: normalizeOptionalText(option.recommendedAction),
    credentialStatus: normalizeOptionalText(option.credentialStatus)?.toUpperCase(),
    healthOutcome: normalizeOptionalText(option.healthOutcome)?.toUpperCase(),
    environment: normalizeText(option.environment, 'ALL').toUpperCase(),
    scope: normalizeText(option.scope, 'TENANT').toUpperCase(),
  }
}

export function normalizeAgentOptionsResponse(response: AgentOptionsResponse): AgentOptionsResponse {
  return {
    ...response,
    requiredCapabilities: normalizeArray(response.requiredCapabilities).map((capability) => normalizeText(capability).toUpperCase()).filter(Boolean),
    selectable: normalizeArray(response.selectable).map(normalizeAgentOption),
    blocked: normalizeArray(response.blocked).map(normalizeAgentOption),
    currentSelection: response.currentSelection ? normalizeAgentOption(response.currentSelection) : undefined,
  }
}

function normalizeRetrievalStage(stage: RetrievalStage): RetrievalStage {
  return {
    ...stage,
    stage: normalizeText(stage.stage, 'UNKNOWN_STAGE'),
    resultCount: normalizeNumber(stage.resultCount),
    relaxedFields: normalizeArray(stage.relaxedFields),
    durationMs: normalizeNumber(stage.durationMs),
  }
}

function normalizeProcessingStage(stage: ProcessingStage): ProcessingStage {
  return {
    ...stage,
    stage: normalizeText(stage.stage, 'UNKNOWN_STAGE'),
    status: normalizeText(stage.status, 'UNKNOWN').toUpperCase(),
    durationMs: normalizeNumber(stage.durationMs),
    code: normalizeOptionalText(stage.code),
    detail: normalizeOptionalText(stage.detail),
    agentId: normalizeOptionalText(stage.agentId),
    providerId: normalizeOptionalText(stage.providerId),
    model: normalizeOptionalText(stage.model),
  }
}

function normalizeRecord(value: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function normalizeNumber(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function normalizeText(value: string | null | undefined, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function normalizeOptionalText(value: string | null | undefined): string | undefined {
  const normalized = normalizeText(value)
  return normalized || undefined
}
